import React, { useState, useEffect } from 'react';
import PageContainer from '../components/containers/PageContainer';
import {
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    IconButton,
    Stack,
    Box,
    TableCell,
    TableRow,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import api from '../config/axios';
import { toast } from 'react-toastify';
import Field from '../components/ui/Field';
import CustomTable from '../components/ui/CustomTable';
import ModalContainer from '../components/containers/ModalContainer';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StatusComp from '../components/common/StatusComp';

const CryptoSettings = () => {
    // Platform Wallets State
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal States
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [qrCodeData, setQrCodeData] = useState('');
    const [showQRModal, setShowQRModal] = useState(false);

    // Form States
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [formData, setFormData] = useState({
        coin: '',
        mainAddress: '',
        minConfirmations: 6,
        otp: '',
    });

    // Funding Wallet State
    const [fundingWallet, setFundingWallet] = useState({
        address: '',
        balance: '0.00',
    });

    // Funding Transactions State
    const [transactions, setTransactions] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Available coins
    const availableCoins = ['BTC', 'ERC20-USDT', 'ERC20-USDC'];

    useEffect(() => {
        fetchWallets();
        fetchFundingWallet();
        fetchTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit]);

    // Fetch Platform Wallets
    const fetchWallets = async () => {
        try {
            const res = await api.get('/admin-settings/get-address');
            setWallets(res.data.data || []);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to fetch wallets');
        }
    };

    // Fetch Funding Wallet
    const fetchFundingWallet = async () => {
        try {
            // TODO: Replace with actual API endpoint when ready
            const res = await api.get('/admin-settings/get-funding-wallet');
            setFundingWallet({
                address: res.data.address || '',
                balance: res.data.balance || '0.00',
            });
        } catch (err) {
            console.error(err);
            // Don't show error for placeholder API
        }
    };

    // Fetch Funding Transactions
    const fetchTransactions = async () => {
        try {
            // TODO: Replace with actual API endpoint when ready
            const res = await api.get(
                `/admin-settings/get-funding-transactions?page=${page}&limit=${limit}&walletType=ETHEREUM`
            );
            setTransactions(res.data.transactions || []);
            setTotalPages(res.data.totalPages || 0);
        } catch (err) {
            console.error(err);
            // Don't show error for placeholder API
        }
    };

    // Create Wallet
    const handleCreateWallet = async () => {
        if (!formData.coin || !formData.mainAddress || !formData.minConfirmations) {
            toast.error('Please fill all required fields');
            return;
        }

        setLoading(true);
        try {
            const res = await api.post('/admin-settings/add-address', {
                coin: formData.coin,
                mainAddress: formData.mainAddress,
                minConfirmations: formData.minConfirmations,
            });

            if (res.data.success) {
                setQrCodeData(res.data.qrCode);
                setShowQRModal(true);
                setShowCreateModal(false);
                setFormData({ coin: '', mainAddress: '', minConfirmations: 6, otp: '' });
                fetchWallets();
                toast.success(res.data.message);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to create wallet');
        } finally {
            setLoading(false);
        }
    };

    // Update Wallet
    const handleUpdateWallet = async () => {
        if (!formData.otp) {
            toast.error('Please enter OTP code');
            return;
        }

        setLoading(true);
        try {
            const res = await api.put('/admin-settings/update-address', {
                coin: selectedWallet.coin,
                mainAddress: formData.mainAddress,
                minConfirmations: formData.minConfirmations,
                otp: formData.otp,
            });

            if (res.data.success) {
                setShowEditModal(false);
                setFormData({ coin: '', mainAddress: '', minConfirmations: 6, otp: '' });
                setSelectedWallet(null);
                fetchWallets();
                toast.success(res.data.message);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to update wallet');
        } finally {
            setLoading(false);
        }
    };

    // Delete Wallet
    const handleDeleteWallet = async () => {
        if (!formData.otp) {
            toast.error('Please enter OTP code');
            return;
        }

        setLoading(true);
        try {
            const res = await api.post('/admin-settings/delete-address', {
                coin: selectedWallet.coin,
                otp: formData.otp,
            });

            if (res.data.success) {
                setShowDeleteModal(false);
                setFormData({ coin: '', mainAddress: '', minConfirmations: 6, otp: '' });
                setSelectedWallet(null);
                fetchWallets();
                toast.success(res.data.message);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to delete wallet');
        } finally {
            setLoading(false);
        }
    };

    // Copy to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    // Get available coins for creation (excluding already configured ones)
    const getAvailableCoinsForCreate = () => {
        const configuredCoins = wallets.map((w) => w.coin);
        return availableCoins.filter((coin) => !configuredCoins.includes(coin));
    };

    return (
        <PageContainer title="Crypto Settings" desc="Manage platform wallets and funding">
            {/* Platform Wallets Section */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary' }}>
                        Platform Wallets
                    </Typography>
                    {getAvailableCoinsForCreate().length > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => {
                                setFormData({ coin: '', mainAddress: '', minConfirmations: 6, otp: '' });
                                setShowCreateModal(true);
                            }}>
                            Add Wallet
                        </Button>
                    )}
                </Stack>

                <Grid container spacing={3}>
                    {wallets.length === 0 && (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="body1" color="text.secondary" textAlign="center">
                                        No wallets configured yet. Click "Add Wallet" to configure a platform wallet.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {wallets.map((wallet) => (
                        <Grid item xs={12} md={4} key={wallet._id}>
                            <Card>
                                <CardContent>
                                    <Stack spacing={2}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="h6" sx={{ color: 'primary.main' }}>
                                                {wallet.coin}
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedWallet(wallet);
                                                            setFormData({
                                                                coin: wallet.coin,
                                                                mainAddress: wallet.mainAddress,
                                                                minConfirmations: wallet.minConfirmations,
                                                                otp: '',
                                                            });
                                                            setShowEditModal(true);
                                                        }}>
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedWallet(wallet);
                                                            setFormData({
                                                                coin: '',
                                                                mainAddress: '',
                                                                minConfirmations: 6,
                                                                otp: '',
                                                            });
                                                            setShowDeleteModal(true);
                                                        }}>
                                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Address
                                            </Typography>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                {wallet.mainAddress}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Min Confirmations
                                            </Typography>
                                            <Typography variant="body2">{wallet.minConfirmations}</Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                2FA Enabled
                                            </Typography>
                                            <Typography variant="body2">
                                                {wallet.isTwoFactorEnabled ? 'Yes' : 'No'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Funding Wallet Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                    Funding Wallet (ETH)
                </Typography>
                <Card>
                    <CardContent>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Field
                                    label="Wallet Address"
                                    value={fundingWallet.address}
                                    disabled
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => copyToClipboard(fundingWallet.address)}
                                                    disabled={!fundingWallet.address}>
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Current Balance" value={`${fundingWallet.balance} ETH`} disabled />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>

            {/* Funding Transactions Section */}
            <Box>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                    Funding Wallet Transactions
                </Typography>
                <CustomTable
                    fields={['TX HASH', 'TYPE', 'FROM', 'TO', 'AMOUNT', 'STATUS', 'DATE']}
                    dense={false}
                    pagination
                    count={totalPages}
                    page={page}
                    setPage={setPage}>
                    {transactions.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={7} align="center">
                                <Typography variant="body2" color="text.secondary">
                                    No transactions found
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        transactions.map((tx) => (
                            <TableRow key={tx._id}>
                                <TableCell sx={{ color: 'primary.main', maxWidth: 150 }}>
                                    <Tooltip title={tx.txHash}>
                                        <span>
                                            {tx.txHash.substring(0, 10)}...{tx.txHash.substring(tx.txHash.length - 8)}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{tx.transactionType}</TableCell>
                                <TableCell sx={{ maxWidth: 150 }}>
                                    <Tooltip title={tx.walletAddress}>
                                        <span>
                                            {tx.walletAddress.substring(0, 10)}...
                                            {tx.walletAddress.substring(tx.walletAddress.length - 8)}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 150 }}>
                                    <Tooltip title={tx.destinationAddress}>
                                        <span>
                                            {tx.destinationAddress.substring(0, 10)}...
                                            {tx.destinationAddress.substring(tx.destinationAddress.length - 8)}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{tx.amount} ETH</TableCell>
                                <TableCell>
                                    <StatusComp status={tx.status.toLowerCase()} />
                                </TableCell>
                                <TableCell>{new Date(tx.createdAt).toLocaleString()}</TableCell>
                            </TableRow>
                        ))
                    )}
                </CustomTable>
            </Box>

            {/* Create Wallet Modal */}
            <ModalContainer
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Add Platform Wallet"
                action={handleCreateWallet}
                actionText="Create"
                loading={loading}>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Field
                        label="Select Coin"
                        select
                        SelectProps={{ native: true }}
                        value={formData.coin}
                        onChange={(e) => setFormData({ ...formData, coin: e.target.value })}>
                        <option value="">Select a coin</option>
                        {getAvailableCoinsForCreate().map((coin) => (
                            <option key={coin} value={coin}>
                                {coin}
                            </option>
                        ))}
                    </Field>
                    <Field
                        label="Main Address"
                        placeholder="Enter wallet address"
                        value={formData.mainAddress}
                        onChange={(e) => setFormData({ ...formData, mainAddress: e.target.value })}
                    />
                    <Field
                        label="Minimum Confirmations"
                        type="number"
                        value={formData.minConfirmations}
                        onChange={(e) => setFormData({ ...formData, minConfirmations: parseInt(e.target.value) })}
                    />
                    <Typography variant="caption" color="text.secondary">
                        After creating, you will receive a QR code to scan with Google Authenticator for 2FA setup.
                    </Typography>
                </Stack>
            </ModalContainer>

            {/* QR Code Modal */}
            <ModalContainer open={showQRModal} onClose={() => setShowQRModal(false)} title="Scan QR Code" hideActions>
                <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                    <Typography variant="body1" textAlign="center">
                        Scan this QR code with Google Authenticator to enable 2FA for this wallet
                    </Typography>
                    {qrCodeData && (
                        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                            <img src={qrCodeData} alt="QR Code" style={{ maxWidth: '100%' }} />
                        </Box>
                    )}
                    <Typography variant="caption" color="warning.main" textAlign="center">
                        Save this QR code securely. You won't be able to see it again.
                    </Typography>
                    <Button variant="contained" onClick={() => setShowQRModal(false)} sx={{ mt: 2 }}>
                        Done
                    </Button>
                </Stack>
            </ModalContainer>

            {/* Edit Wallet Modal */}
            <ModalContainer
                open={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedWallet(null);
                }}
                title="Edit Platform Wallet"
                action={handleUpdateWallet}
                actionText="Update"
                loading={loading}>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Field label="Coin" value={formData.coin} disabled />
                    <Field
                        label="Main Address"
                        placeholder="Enter wallet address"
                        value={formData.mainAddress}
                        onChange={(e) => setFormData({ ...formData, mainAddress: e.target.value })}
                    />
                    <Field
                        label="Minimum Confirmations"
                        type="number"
                        value={formData.minConfirmations}
                        onChange={(e) => setFormData({ ...formData, minConfirmations: parseInt(e.target.value) })}
                    />
                    <Field
                        label="Google Authenticator Code"
                        placeholder="Enter 6-digit code"
                        value={formData.otp}
                        onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                        required
                    />
                    <Typography variant="caption" color="text.secondary">
                        Enter your Google Authenticator code to verify this action
                    </Typography>
                </Stack>
            </ModalContainer>

            {/* Delete Wallet Modal */}
            <ModalContainer
                open={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedWallet(null);
                }}
                title="Delete Platform Wallet"
                hideActions>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Typography variant="body1" color="error">
                        Are you sure you want to delete the {selectedWallet?.coin} wallet? This action cannot be undone.
                    </Typography>
                    <Field
                        label="Google Authenticator Code"
                        placeholder="Enter 6-digit code"
                        value={formData.otp}
                        onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                        required
                    />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedWallet(null);
                            }}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="error" onClick={handleDeleteWallet} disabled={loading}>
                            Delete
                        </Button>
                    </Stack>
                </Stack>
            </ModalContainer>
        </PageContainer>
    );
};

export default CryptoSettings;
